import { useDialogPortal, useTransactionFees, useSimpleGasEstimation, useTransactionExecution } from "@/hooks";
import { withdrawUnwoundEventEmitter, WITHDRAW_UNWOUND_EVENTS } from "@/events";
import { estimateWithdrawTransactionGas, gatewayWithdrawTransaction } from "@/services";
import type { WalletClient } from "viem";
import type { TransactionHash } from "@/types";
import { useQueryClient } from "@tanstack/react-query";
import { QUERY_KEYS } from "@/config";
import { useAccount } from "wagmi";

interface WithdrawUnwoundTransactionProps {
    unwindingTimestamp: number;
}

export function useWithdrawUnwoundTransaction({ unwindingTimestamp }: WithdrawUnwoundTransactionProps) {
    const { closeDialog } = useDialogPortal();
    const { address } = useAccount();

    const {
        gasEstimate,
        isEstimatingGas,
        error: gasError,
    } = useSimpleGasEstimation({
        estimateGasFn: (walletClient: WalletClient) => estimateWithdrawTransactionGas(unwindingTimestamp, walletClient),
    });

    const { gasPriceUSD } = useTransactionFees({
        estimatedGas: gasEstimate,
    });

    const { invalidateQueries } = useQueryClient();
    const {
        transactionHash,
        isExecuting,
        error: transactionError,
        executeTransaction: executeWithdrawUnwoundTransaction,
    } = useTransactionExecution({
        executeTransactionFn: (walletClient) => gatewayWithdrawTransaction(unwindingTimestamp, walletClient),
        onSuccess: (hash: TransactionHash) => {
            withdrawUnwoundEventEmitter.emit(WITHDRAW_UNWOUND_EVENTS.SUCCESS, {
                transactionHash: hash,
                unwindingTimestamp: unwindingTimestamp,
            });
            closeDialog();
            invalidateQueries({
                queryKey: QUERY_KEYS.UNWINDING.USER(address),
            });
        },
        errorMessage: "Failed to withdraw tokens",
    });

    return {
        unwindingTimestamp,
        gasPriceUSD,
        transactionHash,
        executeWithdrawUnwoundTransaction,
        isLoading: isExecuting,
        isEstimatingGas,
        error: gasError || transactionError,
        gasEstimate,
    };
}
